import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAtom } from 'jotai';
import {
  plugPowerHistoryAtom,
  ChargerHistoryRow,
  BuildingIDProps,
  chargerShowAvailableOnlyAtom,
  chargerScaleModeAtom,
} from '../atoms/chargers';
import PlugPowerGraph from '../charts/PlugPowerGraph';
import { DateTime } from 'luxon';
import { Card, CardContent } from '@mui/material';
import GraphTimePicker from '../buttons/GraphTimePicker';
import DownloadButton from '../buttons/DownloadButton';
import useWebSocket from '../hooks/wsHook';
import { fpmEndpointLink, fpmWebsocketLink } from '../../../index';
import { listDevices } from './plugCommissionHandler';
import { sharedGraphTimeRangeAtom, type GraphRangeLabel } from '../atoms/graphTimeRange';

const PlugPowerGraphHandler: React.FC<BuildingIDProps> = ({ BuildingID }) => {
  const [historyMap, setHistoryMap] = useAtom(plugPowerHistoryAtom);
  const [showAvailableOnly] = useAtom(chargerShowAvailableOnlyAtom);
  const [scaleMode] = useAtom(chargerScaleModeAtom);
  const [sharedTimeRange, setSharedTimeRange] = useAtom(sharedGraphTimeRangeAtom);
  const { messages } = useWebSocket(fpmWebsocketLink, ['charger']);
  const lastProcessedIndexRef = useRef(-1);
  const startDateTime = DateTime.fromISO(sharedTimeRange.startISO, { zone: 'America/New_York' });
  const endDateTime = DateTime.fromISO(sharedTimeRange.endISO, { zone: 'America/New_York' });
  const selectedRange = sharedTimeRange.selectedRange;
  const setStartDateTime = (date: DateTime) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      startISO: date.setZone('America/New_York').toISO() ?? prev.startISO,
    }));
  };
  const setEndDateTime = (date: DateTime) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      endISO: date.setZone('America/New_York').toISO() ?? prev.endISO,
    }));
  };
  const setSelectedRange = (label: string) => {
    setSharedTimeRange((prev) => ({
      ...prev,
      selectedRange: label as GraphRangeLabel,
    }));
  };
  const [suppressUntilFetch, setSuppressUntilFetch] = useState<boolean>(false);
  const prevStartRef = useRef<DateTime>(startDateTime);
  const prevEndRef = useRef<DateTime>(endDateTime);

  const { isFetching } = useQuery({
    queryKey: ['fpmPlugPowerHistory', BuildingID, sharedTimeRange.startISO, sharedTimeRange.endISO],
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!BuildingID,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const response = await axios.get<ChargerHistoryRow[]>(
        `${fpmEndpointLink}charger-graph-history`,
        {
          params: {
            BuildingID,
            start: startDateTime.toISO(),
            end: endDateTime.toISO(),
          },
          signal,
        }
      );
      const data = response.data;
      const buildingKey = BuildingID!.toLowerCase();
      const filtered = (Array.isArray(data) ? data : []).filter(
        (r) => r.BuildingID?.toLowerCase() === buildingKey
      );

      if (selectedRange === 'Custom') {
        const startMs = startDateTime.toMillis();
        const endMs = endDateTime.toMillis();
        setHistoryMap((prev) => ({
          ...prev,
          [buildingKey]: filtered.filter((d) => {
            const ms = d.TimestampMs ?? (d.Timestamp ? new Date(d.Timestamp.replace(' ', 'T') + 'Z').getTime() : 0);
            return ms >= startMs && ms <= endMs;
          }),
        }));
      } else {
        const apiMaxMs = filtered.length
          ? Math.max(...filtered.map((d) => d.TimestampMs ?? (d.Timestamp ? new Date(String(d.Timestamp).replace(' ', 'T') + 'Z').getTime() : 0)))
          : 0;
        setHistoryMap((prev) => {
          const existing = prev[buildingKey] || [];
          const wsRows = existing.filter((r) => (r.TimestampMs ?? 0) > apiMaxMs);
          const merged = [...filtered, ...wsRows].sort((a, b) => ((a.TimestampMs ?? 0) - (b.TimestampMs ?? 0)));
          return { ...prev, [buildingKey]: merged };
        });
      }
      setSuppressUntilFetch(false);
      return filtered;
    },
  });

  const { data: devices = [] } = useQuery({
    queryKey: ['fpmMatterDevicesForPlugGraph', BuildingID],
    queryFn: () => listDevices(BuildingID || 'all'),
    enabled: !!BuildingID,
  });
  const { data: setupConfig } = useQuery({
    queryKey: ['fpmSetupSimulationForPlugGraph', BuildingID],
    queryFn: async () => {
      const response = await axios.get<{ nPlugs?: number }>(`${fpmEndpointLink}setup-simulation`, {
        params: { BuildingID },
      });
      return response.data;
    },
    enabled: !!BuildingID,
    staleTime: 30000,
  });
  const { data: chargerGraphSnapshot = [] } = useQuery({
    queryKey: ['fpmChargerGraphSlotCountForPlugGraph', BuildingID],
    queryFn: async () => {
      const response = await axios.get<Array<{ NChans?: number; CPower?: unknown }>>(
        `${fpmEndpointLink}charger-graph`,
        { params: { BuildingID } }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!BuildingID,
  });

  useEffect(() => {
    const buildingKey = BuildingID?.toLowerCase() || '';
    const hasRangeChanged =
      prevStartRef.current.toMillis() !== startDateTime.toMillis() ||
      prevEndRef.current.toMillis() !== endDateTime.toMillis();

    prevStartRef.current = startDateTime;
    prevEndRef.current = endDateTime;

    if (!hasRangeChanged) return;

    if (selectedRange === 'Custom') {
      setSuppressUntilFetch(true);
      setHistoryMap((prev) => ({ ...prev, [buildingKey]: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDateTime, endDateTime, BuildingID, selectedRange, setHistoryMap]);

  useEffect(() => {
    if (messages.length === 0 || !BuildingID || selectedRange === 'Custom') return;

    const newMessages = messages.slice(lastProcessedIndexRef.current + 1);
    lastProcessedIndexRef.current = messages.length - 1;

    newMessages.forEach((messageObj) => {
      try {
        const { channel, message } = messageObj;
        if (channel !== 'charger' || !message || typeof message !== 'object' || !('BuildingID' in message)) return;

        const msg = message as { BuildingID?: string; Timestamp?: string; NCharging?: number; NWaiting?: number; CState?: unknown; CPower?: unknown; CEnergy?: unknown };
        if (msg.BuildingID?.toLowerCase() !== BuildingID?.toLowerCase()) return;

        const CState = Array.isArray(msg.CState) ? msg.CState : (typeof msg.CState === 'string' ? JSON.parse(msg.CState) : []);
        const CPower = Array.isArray(msg.CPower) ? msg.CPower : (typeof msg.CPower === 'string' ? JSON.parse(msg.CPower) : []);
        const CEnergy = Array.isArray(msg.CEnergy) ? msg.CEnergy : (typeof msg.CEnergy === 'string' ? JSON.parse(msg.CEnergy) : []);

        const timestampMs = msg.Timestamp
          ? DateTime.fromISO(msg.Timestamp, { zone: 'utc' }).toMillis()
          : DateTime.utc().toMillis();

        setHistoryMap((prev) => {
          const buildingKey = BuildingID.toLowerCase();
          const existing = prev[buildingKey] || [];
          if (existing.some((r) => r.TimestampMs === timestampMs)) return prev;
          const row: ChargerHistoryRow = {
            BuildingID: msg.BuildingID!,
            Timestamp: DateTime.fromMillis(timestampMs, { zone: 'utc' }).toFormat('yyyy-MM-dd HH:mm:ss'),
            TimestampMs: timestampMs,
            NCharging: msg.NCharging ?? 0,
            NWaiting: msg.NWaiting ?? 0,
            CState,
            CPower,
            CEnergy,
          };
          return { ...prev, [buildingKey]: [...existing, row] };
        });
      } catch (err) {
        console.error('Failed to process charger WebSocket message:', err);
      }
    });
  }, [messages, BuildingID, selectedRange, setHistoryMap]);

  useEffect(() => {
    lastProcessedIndexRef.current = -1;
  }, [BuildingID, selectedRange]);

  const historyData = BuildingID ? historyMap[BuildingID.toLowerCase()] ?? [] : [];
  const maxPlugsInHistory = historyData.reduce((acc, row) => Math.max(acc, row.CPower?.length ?? 0), 0);
  const assignedPlugNums = new Set<number>();
  const unavailablePlugNums = new Set<number>();
  for (const d of devices) {
    if (d.PlugNum == null) continue;
    assignedPlugNums.add(d.PlugNum);
    if (d.Available === 0) unavailablePlugNums.add(d.PlugNum);
  }
  const maxAssigned = assignedPlugNums.size > 0 ? Math.max(...Array.from(assignedPlugNums)) : 0;
  const setupConfigRecord = setupConfig as Record<string, unknown> | undefined;
  const configuredNPlugs = Number(setupConfigRecord?.nPlugs ?? setupConfigRecord?.Nplugs ?? setupConfigRecord?.nplugs ?? 0);
  const nChansFromSnapshot = chargerGraphSnapshot.reduce((max, row) => {
    const nChans = Number(row?.NChans ?? 0);
    const cPowerLen = Array.isArray(row?.CPower) ? row.CPower.length : 0;
    return Math.max(max, Number.isFinite(nChans) ? nChans : 0, cPowerLen);
  }, 0);
  const maxSlots = Math.max(
    maxPlugsInHistory,
    maxAssigned,
    Number.isFinite(configuredNPlugs) ? configuredNPlugs : 0,
    nChansFromSnapshot
  );
  for (let plugNum = 1; plugNum <= maxSlots; plugNum++) {
    if (!assignedPlugNums.has(plugNum)) unavailablePlugNums.add(plugNum);
  }

  const downloadRows = historyData.map((r) => {
    const row: Record<string, string | number> = {
      TimestampMs: r.TimestampMs ?? new Date(String(r.Timestamp).replace(' ', 'T') + 'Z').getTime(),
    };
    (r.CPower ?? []).forEach((p, i) => {
      row[`Plug${i + 1}`] = p ?? 0;
    });
    return row;
  });
  const valueFields = historyData.length
    ? (historyData[0].CPower ?? []).map((_, i) => `Plug${i + 1}`)
    : [];

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <CardContent sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, width: '100%', padding: '16px' }}>
        <PlugPowerGraph
          BuildingID={BuildingID}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          isLoading={isFetching || suppressUntilFetch}
          forceNoDataOnEmpty={selectedRange === 'Custom'}
          unavailablePlugNums={Array.from(unavailablePlugNums)}
          maxPlugsOverride={maxSlots}
          showAvailableOnly={showAvailableOnly}
          scaleMode={scaleMode}
        />
      </CardContent>
      <CardContent
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <GraphTimePicker
            startDateTime={startDateTime}
            endDateTime={endDateTime}
            setStartDateTime={setStartDateTime}
            setEndDateTime={setEndDateTime}
            selectedRange={selectedRange}
            setSelectedRange={setSelectedRange}
            isLoading={isFetching || suppressUntilFetch}
          />
          <DownloadButton
            data={downloadRows}
            filename={`${BuildingID}_plug_power_${startDateTime.toFormat('yyyy-MM-dd')}_to_${endDateTime.toFormat('yyyy-MM-dd')}`}
            timestampField="TimestampMs"
            valueFields={valueFields}
            disabled={!historyData.length}
            timezone="America/New_York"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default PlugPowerGraphHandler;
